import React from 'react';
import { useParams } from 'react-router-dom';
import './BlogPost.css';

const BlogPost = () => {
  const { id } = useParams();
  
  // This would typically come from an API or database
  const blogPosts = {
    1: {
      title: "Understanding Gold Purity: 24K, 22K, and 18K Explained",
      content: (
        <>
          <p>Gold purity is measured in karats, with 24K being the purest form of gold. Understanding gold purity is crucial for both investors and jewelry buyers, as it directly affects the value and durability of gold items.</p>

          <h2>What is Gold Purity?</h2>
          <p>Gold purity refers to the amount of pure gold present in an alloy. Pure gold is 24 karats (24K), meaning it's 99.9% gold. However, pure gold is too soft for most practical uses, which is why it's often mixed with other metals to create alloys.</p>

          <h2>Common Gold Purity Levels</h2>
          <h3>24K Gold (99.9% Pure)</h3>
          <p>24K gold is the purest form available, containing 99.9% gold. It has a rich, deep yellow color and is highly valuable. However, its softness makes it unsuitable for most jewelry as it can easily scratch or bend.</p>

          <h3>22K Gold (91.7% Pure)</h3>
          <p>22K gold contains 91.7% gold and 8.3% other metals. It's commonly used in traditional jewelry, especially in South Asian countries. While more durable than 24K, it's still relatively soft and requires careful handling.</p>

          <h3>18K Gold (75% Pure)</h3>
          <p>18K gold, containing 75% gold, is the most popular choice for fine jewelry. It offers an excellent balance between purity and durability. The remaining 25% is typically a mix of silver, copper, and sometimes palladium or zinc.</p>

          <h2>How Purity Affects Value</h2>
          <p>The higher the karat, the more valuable the gold. However, the price difference isn't linear. For example, 24K gold isn't twice as expensive as 12K gold. The value is determined by both the gold content and the current market price of gold.</p>

          <h2>Choosing the Right Purity</h2>
          <p>When selecting gold, consider your needs:</p>
          <ul>
            <li><strong>Investment:</strong> 24K gold is preferred for pure investment purposes</li>
            <li><strong>Jewelry:</strong> 18K or 22K offers the best balance of beauty and durability</li>
            <li><strong>Daily Wear:</strong> 14K or 18K is more practical for items worn regularly</li>
          </ul>

          <h2>Testing Gold Purity</h2>
          <p>Several methods can verify gold purity:</p>
          <ul>
            <li>Hallmark stamps indicating karat value</li>
            <li>Acid testing kits</li>
            <li>Electronic gold testers</li>
            <li>Professional appraisal</li>
          </ul>

          <p>Understanding gold purity is essential for making informed decisions when buying or selling gold. Whether you're investing or purchasing jewelry, knowing the karat value helps ensure you get the right quality for your needs.</p>
        </>
      ),
      date: "March 15, 2024",
      readTime: "5 min read",
      category: "Gold Basics",
      author: "Gold Expert"
    },
    2: {
      title: "How to Calculate Gold Price: A Comprehensive Guide",
      content: (
        <>
          <p>Calculating gold prices involves understanding several factors including purity, weight, and current market rates. This guide will help you understand how to determine the value of your gold items accurately.</p>

          <h2>Understanding the Components</h2>
          <p>Gold price calculation involves three main components:</p>
          <ul>
            <li><strong>Current Market Price:</strong> The live price of gold per ounce or gram</li>
            <li><strong>Purity (Karat):</strong> The percentage of pure gold in the item</li>
            <li><strong>Weight:</strong> The total weight of the item in grams or ounces</li>
          </ul>

          <h2>Step-by-Step Calculation</h2>
          <h3>1. Determine the Current Market Price</h3>
          <p>The first step is to find the current market price of gold. This can be found on financial websites, gold trading platforms, or through your local gold dealer. Prices are typically quoted per troy ounce or per gram.</p>

          <h3>2. Convert to Your Preferred Unit</h3>
          <p>If the price is in troy ounces, you'll need to convert it to grams (1 troy ounce = 31.1035 grams). Most jewelry and small items are weighed in grams.</p>

          <h3>3. Calculate the Purity Factor</h3>
          <p>Convert the karat value to a percentage:</p>
          <ul>
            <li>24K = 99.9% pure</li>
            <li>22K = 91.7% pure</li>
            <li>18K = 75% pure</li>
            <li>14K = 58.3% pure</li>
          </ul>

          <h3>4. Apply the Formula</h3>
          <p>The basic formula is:</p>
          <p><strong>Price = (Weight × Purity Factor × Current Market Price) + Making Charges</strong></p>

          <h2>Additional Factors to Consider</h2>
          <ul>
            <li><strong>Making Charges:</strong> The cost of crafting the item</li>
            <li><strong>Designer Value:</strong> Additional value for unique or designer pieces</li>
            <li><strong>Market Premium:</strong> Additional charges based on market conditions</li>
            <li><strong>Taxes:</strong> Applicable taxes and duties</li>
          </ul>

          <h2>Practical Example</h2>
          <p>Let's calculate the price of a 22K gold chain weighing 10 grams when gold is trading at $60 per gram:</p>
          <ul>
            <li>Weight: 10 grams</li>
            <li>Purity: 91.7% (22K)</li>
            <li>Base Price: 10 × 0.917 × $60 = $550.20</li>
            <li>Making Charges (typically 10-15%): $55.02</li>
            <li>Total Price: $605.22</li>
          </ul>

          <p>Remember that these calculations provide an estimate. Actual prices may vary based on market conditions, location, and dealer policies.</p>
        </>
      ),
      date: "March 10, 2024",
      readTime: "7 min read",
      category: "Gold Trading",
      author: "Market Analyst"
    },
    3: {
      title: "Factors Affecting Gold Prices in 2024",
      content: (
        <>
          <p>Gold prices are influenced by various economic and geopolitical factors. Understanding these factors can help investors make better decisions about when to buy or sell gold.</p>

          <h2>Economic Factors</h2>
          <h3>Inflation and Interest Rates</h3>
          <p>Gold is often seen as a hedge against inflation. When inflation rises, the value of currency decreases, making gold more attractive. Conversely, when interest rates rise, gold prices may fall as investors move to interest-bearing assets.</p>

          <h3>US Dollar Strength</h3>
          <p>Since gold is priced in US dollars, the strength of the dollar has an inverse relationship with gold prices. A stronger dollar makes gold more expensive for foreign buyers, potentially reducing demand.</p>

          <h3>Central Bank Policies</h3>
          <p>Central banks' gold buying and selling activities significantly impact prices. When central banks increase their gold reserves, it can drive prices up.</p>

          <h2>Geopolitical Factors</h2>
          <h3>Political Instability</h3>
          <p>During times of political uncertainty or conflict, investors often turn to gold as a safe haven, driving up prices.</p>

          <h3>Trade Relations</h3>
          <p>International trade tensions and agreements can affect gold prices by influencing currency values and economic stability.</p>

          <h2>Market Factors</h2>
          <h3>Supply and Demand</h3>
          <p>The basic economic principle of supply and demand plays a crucial role in gold pricing. Factors affecting supply include:</p>
          <ul>
            <li>Mining production</li>
            <li>Recycling rates</li>
            <li>Central bank sales</li>
          </ul>

          <h3>Investment Demand</h3>
          <p>Investment demand comes from various sources:</p>
          <ul>
            <li>Gold ETFs and funds</li>
            <li>Physical gold purchases</li>
            <li>Futures and options trading</li>
          </ul>

          <h2>2024 Specific Factors</h2>
          <ul>
            <li>Global economic recovery post-pandemic</li>
            <li>Central bank digital currency developments</li>
            <li>Climate change impact on mining operations</li>
            <li>Technological advancements in gold extraction</li>
          </ul>

          <p>Understanding these factors can help investors make more informed decisions about gold investments. However, it's important to remember that gold prices can be volatile and are influenced by multiple, sometimes conflicting, factors.</p>
        </>
      ),
      date: "March 5, 2024",
      readTime: "6 min read",
      category: "Market Analysis",
      author: "Financial Expert"
    },
    4: {
      title: "Gold Investment: Tips for Beginners",
      content: (
        <>
          <p>Investing in gold can be a great way to diversify your portfolio and protect your wealth. This guide will help beginners understand the basics of gold investment and make informed decisions.</p>

          <h2>Why Invest in Gold?</h2>
          <p>Gold has been a valuable asset for centuries due to its unique properties:</p>
          <ul>
            <li><strong>Store of Value:</strong> Maintains purchasing power over time</li>
            <li><strong>Hedge Against Inflation:</strong> Tends to rise when inflation increases</li>
            <li><strong>Portfolio Diversification:</strong> Low correlation with other assets</li>
            <li><strong>Liquidity:</strong> Can be easily bought and sold</li>
          </ul>

          <h2>Different Ways to Invest in Gold</h2>
          <h3>Physical Gold</h3>
          <p>Investing in physical gold includes:</p>
          <ul>
            <li>Gold coins and bars</li>
            <li>Jewelry (though not ideal for pure investment)</li>
            <li>Gold certificates</li>
          </ul>

          <h3>Paper Gold</h3>
          <p>Paper gold investments include:</p>
          <ul>
            <li>Gold ETFs (Exchange Traded Funds)</li>
            <li>Gold mutual funds</li>
            <li>Gold futures and options</li>
            <li>Gold mining stocks</li>
          </ul>

          <h2>Key Considerations for Beginners</h2>
          <h3>Investment Goals</h3>
          <p>Define your investment objectives:</p>
          <ul>
            <li>Short-term trading vs. long-term holding</li>
            <li>Capital preservation vs. growth</li>
            <li>Portfolio percentage allocation</li>
          </ul>

          <h3>Risk Management</h3>
          <p>Important risk management strategies:</p>
          <ul>
            <li>Diversify within gold investments</li>
            <li>Set clear entry and exit points</li>
            <li>Understand market cycles</li>
            <li>Consider storage and insurance costs</li>
          </ul>

          <h2>Common Mistakes to Avoid</h2>
          <ul>
            <li>Investing without proper research</li>
            <li>Timing the market</li>
            <li>Over-investing in gold</li>
            <li>Ignoring storage and insurance costs</li>
            <li>Buying at peak prices due to fear of missing out</li>
          </ul>

          <h2>Getting Started</h2>
          <p>Steps to begin your gold investment journey:</p>
          <ol>
            <li>Research and educate yourself</li>
            <li>Determine your investment amount</li>
            <li>Choose your preferred investment method</li>
            <li>Find a reputable dealer or broker</li>
            <li>Start with a small investment</li>
            <li>Monitor and adjust your strategy</li>
          </ol>

          <p>Remember that gold investment should be part of a diversified portfolio. While gold can provide stability and protection against inflation, it's important to maintain a balanced investment approach.</p>
        </>
      ),
      date: "March 1, 2024",
      readTime: "8 min read",
      category: "Investment",
      author: "Investment Advisor"
    }
  };

  const post = blogPosts[id];

  if (!post) {
    return <div className="blog-post-not-found">Blog post not found</div>;
  }

  return (
    <article className="blog-post">
      <div className="blog-post-container">
        <div className="blog-post-header">
          <span className="blog-post-category">{post.category}</span>
          <span className="blog-post-date">{post.date}</span>
        </div>
        <h1 className="blog-post-title">{post.title}</h1>
        <div className="blog-post-meta">
          <span className="blog-post-author">By {post.author}</span>
          <span className="blog-post-read-time">{post.readTime}</span>
        </div>
        <div className="blog-post-content">
          {post.content}
        </div>
      </div>
    </article>
  );
};

export default BlogPost; 